// =============================================================================
// Categorias por área — quais categorias cada área orça (Planilha, 081)
//
// Pedido do Financeiro de 28/10, item 1: na proposta do ano seguinte o gestor
// recebia toda categoria com gasto no centro de custo dele, inclusive as que
// não são decisão dele (impostos sobre vendas, IRPJ, CSLL). Aqui o Financeiro
// diz, categoria por categoria, quem orça:
//   · todas as áreas (o padrão);
//   · só algumas áreas;
//   · só o Financeiro — não entra em proposta nenhuma, e é orçada direto na
//     Planilha.
// Salvar tira das propostas em montagem as linhas dessa categoria que ainda
// estão com o gestor; a enviada e a aprovada ficam. Quem recusa é o banco.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { buscarCategoriasArea, configurarCategoriaArea, FalhaApi, type CategoriaArea } from "../lib/api.ts";
import { Painel, Vazio } from "../componentes/ui.tsx";
import { Modal } from "../componentes/Modal.tsx";

type Quem = "todas" | "algumas" | "financeiro";
const quemDe = (c: CategoriaArea): Quem => (c.so_financeiro ? "financeiro" : c.centros.length ? "algumas" : "todas");
const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export function CategoriasArea() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["categorias-area"], queryFn: buscarCategoriasArea });
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<"tudo" | Quem>("tudo");
  const [escolhendo, setEscolhendo] = useState<CategoriaArea | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: (x: { c: CategoriaArea; so: boolean; centros: string[] }) => configurarCategoriaArea(x.c.id, x.so, x.centros),
    onSuccess: (r, x) => {
      setEscolhendo(null);
      setAviso(!r.mudou ? `${x.c.nome}: nada mudou.`
        : `${x.c.nome}: salvo.` + (r.linhas_tiradas
          ? ` ${r.linhas_tiradas} ${r.linhas_tiradas === 1 ? "linha saiu" : "linhas saíram"} de propostas que ainda estavam com o gestor.` : ""));
      void qc.invalidateQueries({ queryKey: ["categorias-area"] });
      void qc.invalidateQueries({ queryKey: ["proposta"] });
      void qc.invalidateQueries({ queryKey: ["propostas-areas"] });
    },
    onError: (e) => setAviso((e as FalhaApi).message),
  });

  const centros = q.data?.centros ?? [];
  const codigoDe = useMemo(() => new Map(centros.map((c) => [c.id, c.codigo])), [centros]);
  const gastoDe = useMemo(() => new Map((q.data?.gasto ?? []).map((g) => [g.categoria_id, g.centros])), [q.data]);
  const termo = normalizar(busca);
  const lista = (q.data?.categorias ?? []).filter((c) =>
    (filtro === "tudo" || quemDe(c) === filtro)
    && (!termo || normalizar(`${c.codigo ?? ""} ${c.nome}`).includes(termo)));
  const contar = (k: Quem) => (q.data?.categorias ?? []).filter((c) => quemDe(c) === k).length;
  const codigos = (ids: string[]) => ids.map((id) => codigoDe.get(id) ?? "?").sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  const mudarQuem = (c: CategoriaArea, quem: Quem) => {
    if (quem === "algumas") { setEscolhendo(c); return; }
    salvar.mutate({ c, so: quem === "financeiro", centros: [] });
  };

  if (q.isPending) return <div className="pagina py-6 text-[13px] text-tinta-fraca">Carregando…</div>;
  if (q.isError) return <div className="pagina py-6"><Vazio titulo="Não foi possível carregar" dica={(q.error as FalhaApi).message} /></div>;

  return (
    <div className="pagina pb-20">
      <header className="pt-7 pb-4">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">Orçamento {q.data.ano_base + 1} · propostas das áreas</p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">Categorias por área</h1>
        <p className="mt-1 max-w-3xl text-[14px] text-tinta-fraca">
          Quem orça cada categoria na proposta de {q.data.ano_base + 1}. A categoria só do Financeiro não aparece para gestor
          nenhum: você a orça direto na Planilha. A de algumas áreas só aparece para elas. Ao salvar, a categoria sai das
          propostas que ainda estão com o gestor; o que já foi enviado ou aprovado fica.
        </p>
      </header>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar categoria"
          className="w-64 rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]" />
        {([["tudo", "Todas as categorias"], ["todas", `Todas as áreas (${contar("todas")})`],
          ["algumas", `Algumas áreas (${contar("algumas")})`], ["financeiro", `Só o Financeiro (${contar("financeiro")})`]] as const)
          .map(([k, rotulo]) => (
            <button key={k} type="button" onClick={() => setFiltro(k)}
              className={`rounded-lg px-3 py-1.5 text-[12.5px] font-semibold ${filtro === k ? "bg-azul text-white" : "bg-superficie-2 text-tinta-media"}`}>
              {rotulo}
            </button>
          ))}
      </div>

      {aviso && (
        <p className="mb-3 rounded-lg border border-linha bg-superficie-2 px-3 py-2 text-[12.5px] text-tinta-media">{aviso}</p>
      )}

      <Painel>
        {lista.length === 0 ? (
          <Vazio titulo="Nenhuma categoria" dica="Troque a busca ou o filtro." />
        ) : (
          <table className="w-full border-collapse text-[12.5px]">
            <thead className="text-[11px] font-semibold tracking-wide text-tinta-fraca uppercase">
              <tr className="border-b border-linha">
                <th className="px-2 py-2 text-left">Categoria</th>
                <th className="px-2 py-2 text-left">Teve gasto em {q.data.ano_base}</th>
                <th className="px-2 py-2 text-left">Quem orça</th>
                <th className="px-2 py-2 text-left">Áreas</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => {
                const quem = quemDe(c);
                const gasto = gastoDe.get(c.id) ?? [];
                return (
                  <tr key={c.id} className="border-b border-linha/60 hover:bg-superficie-2/40">
                    <td className="px-2 py-1.5">
                      <span className="num mr-2 text-tinta-fraca">{c.codigo ?? ""}</span>{c.nome}
                    </td>
                    <td className="px-2 py-1.5 text-tinta-media">{gasto.length ? codigos(gasto).join(", ") : <span className="text-tinta-fraca">—</span>}</td>
                    <td className="px-2 py-1.5">
                      <select value={quem} disabled={salvar.isPending} onChange={(e) => mudarQuem(c, e.target.value as Quem)}
                        aria-label={`Quem orça ${c.nome}`}
                        className={`rounded-lg border border-linha bg-superficie px-2 py-1 text-[12.5px] ${quem === "financeiro" ? "font-semibold text-azul" : ""}`}>
                        <option value="todas">Todas as áreas</option>
                        <option value="algumas">Algumas áreas…</option>
                        <option value="financeiro">Só o Financeiro</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      {quem === "algumas" ? (
                        <button type="button" onClick={() => setEscolhendo(c)} className="text-azul underline-offset-2 hover:underline">
                          {codigos(c.centros).join(", ")}
                        </button>
                      ) : <span className="text-tinta-fraca">{quem === "financeiro" ? "nenhuma" : "todas"}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Painel>

      {escolhendo && (
        <ModalAreas
          categoria={escolhendo}
          centros={centros}
          sugeridas={gastoDe.get(escolhendo.id) ?? []}
          salvando={salvar.isPending}
          onFechar={() => setEscolhendo(null)}
          onSalvar={(ids) => salvar.mutate({ c: escolhendo, so: false, centros: ids })}
        />
      )}
    </div>
  );
}

function ModalAreas({
  categoria, centros, sugeridas, salvando, onFechar, onSalvar,
}: {
  categoria: CategoriaArea;
  centros: { id: string; codigo: string; nome: string }[];
  sugeridas: string[];
  salvando: boolean;
  onFechar: () => void;
  onSalvar: (ids: string[]) => void;
}) {
  // Sem áreas escolhidas ainda, começa pelas que tiveram gasto: é quase sempre a resposta.
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set(categoria.centros.length ? categoria.centros : sugeridas));
  const alternar = (id: string) => setMarcadas((m) => { const n = new Set(m); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Modal titulo="Áreas que orçam a categoria" onFechar={onFechar}>
      <p className="text-[13px] text-tinta-media"><strong>{categoria.codigo ?? ""} {categoria.nome}</strong> aparece na proposta só destas áreas.</p>
      <ul className="mt-3 grid grid-cols-2 gap-1">
        {centros.map((c) => (
          <li key={c.id}>
            <label className="flex items-center gap-2 rounded-lg px-2 py-1 text-[13px] hover:bg-superficie-2">
              <input type="checkbox" checked={marcadas.has(c.id)} onChange={() => alternar(c.id)} />
              <span className="num text-tinta-fraca">{c.codigo}</span> {c.nome}
              {sugeridas.includes(c.id) && <span className="text-[10.5px] whitespace-nowrap text-tinta-fraca">(teve gasto)</span>}
            </label>
          </li>
        ))}
      </ul>
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onFechar} className="rounded-lg border border-linha px-4 py-2 text-[13px] font-semibold text-tinta-media">Cancelar</button>
        <button type="button" disabled={marcadas.size === 0 || salvando} onClick={() => onSalvar([...marcadas])}
          className="rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </Modal>
  );
}
